import { Fragment, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Modal, Chip } from "./ui-bits";
import { ClipboardPlus, ChevronDown, ChevronRight } from "lucide-react";

const n = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const TONE = { "In attesa": "gold", Ricevuto: "green", Annullato: "red" };

export function NewPurchaseOrderButton({ onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="new-po-btn">
        <ClipboardPlus size={14}/> Ordina al fornitore
      </button>
      {open && <PurchaseOrderModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }}/>}
    </>
  );
}

function PurchaseOrderModal({ onClose, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [rows, setRows] = useState([]);
  const [qty, setQty] = useState({});
  const [cost, setCost] = useState({});
  const [expected, setExpected] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/suppliers").then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!supplierId) { setRows([]); return; }
    let alive = true;
    setErr("");
    api.get("/purchase-orders/draft", { params: { supplier_id: supplierId } }).then(r => {
      if (!alive) return;
      setRows(r.data);
      setQty(Object.fromEntries(r.data.map(x => [x.material_id, x.quantity > 0 ? String(x.quantity) : ""])));
      setCost(Object.fromEntries(r.data.map(x => [x.material_id, x.cost_per_unit ? String(x.cost_per_unit) : ""])));
    }).catch(() => alive && setErr("Impossibile caricare i materiali del fornitore."));
    return () => { alive = false; };
  }, [supplierId]);

  const lines = rows.filter(r => Number(qty[r.material_id]) > 0)
    .map(r => ({ material_id: r.material_id, quantity: Number(qty[r.material_id]), unit_cost: Number(cost[r.material_id]) || 0 }));
  const total = lines.reduce((s, l) => s + l.quantity * l.unit_cost, 0);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/purchase-orders", { supplier_id: supplierId, expected_date: expected || null, notes: notes || null, lines });
      onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Nuovo ordine al fornitore" wide>
      <div data-testid="po-modal">
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div><label className="label-caps block mb-2">Fornitore</label>
            <select className="input-dark" value={supplierId} onChange={e => setSupplierId(e.target.value)} data-testid="po-supplier">
              <option value="">Scegli il fornitore…</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div><label className="label-caps block mb-2">Consegna prevista</label>
            <input type="date" className="input-dark" value={expected} onChange={e => setExpected(e.target.value)} data-testid="po-expected"/></div>
        </div>
        {supplierId && (rows.length === 0 ? <div className="text-sm opacity-60" data-testid="po-empty">Nessun materiale associato a questo fornitore.</div> : (
          <>
            <p className="text-xs opacity-60 mb-3">Quantità proposte = fabbisogno degli ordini aperti + scorta minima − magazzino − merce già in arrivo.</p>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="tbl w-full text-xs [&_td]:!px-2 [&_th]:!px-2">
                <thead><tr><th className="text-left">Materiale</th><th className="text-right">Magazzino</th><th className="text-right">In arrivo</th><th className="text-right">Proposto</th><th className="text-right">Da ordinare</th><th className="text-right">Prezzo €/u</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.material_id} data-testid={`po-row-${r.material_id}`}>
                      <td><div className="font-medium">{r.name}</div><div className="opacity-60">{[r.code, r.color].filter(Boolean).join(" · ")}</div></td>
                      <td className="text-right whitespace-nowrap">{n(r.stock)} {r.unit}</td>
                      <td className="text-right whitespace-nowrap">{r.incoming > 0 ? `${n(r.incoming)} ${r.unit}` : "—"}</td>
                      <td className="text-right whitespace-nowrap">{r.suggested > 0 ? `${n(r.suggested)} ${r.unit}` : "—"}</td>
                      <td className="text-right"><input type="number" min="0" step="0.01" className="input-dark w-24 text-right py-1" value={qty[r.material_id] ?? ""}
                        onChange={e => setQty({ ...qty, [r.material_id]: e.target.value })} data-testid={`po-qty-${r.material_id}`}/></td>
                      <td className="text-right"><input type="number" min="0" step="0.01" className="input-dark w-24 text-right py-1" value={cost[r.material_id] ?? ""}
                        onChange={e => setCost({ ...cost, [r.material_id]: e.target.value })} data-testid={`po-cost-${r.material_id}`}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ))}
        <div className="mt-4"><label className="label-caps block mb-2">Note</label>
          <input className="input-dark" value={notes} onChange={e => setNotes(e.target.value)} data-testid="po-notes"/></div>
        {err && <div className="text-red-600 text-sm mt-3" data-testid="po-error">{err}</div>}
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm" data-testid="po-summary">{lines.length} materiali · {eur(total)}</div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
            <button type="button" disabled={!supplierId || !lines.length || busy} onClick={save} className="btn-gold" data-testid="po-save">{busy ? "Salvataggio…" : "Salva ordine"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function PurchaseOrdersList() {
  const [status, setStatus] = useState("In attesa");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState({});
  const [listErr, setListErr] = useState("");
  const load = () => api.get("/purchase-orders", { params: { status: status || undefined } }).then(r => setItems(r.data)).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);
  const setSt = async (po, s, msg) => {
    if (msg && !window.confirm(msg)) return;
    setListErr("");
    try { await api.post(`/purchase-orders/${po.id}/status`, { status: s }); }
    catch (e) { setListErr(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita."); }
    load();
  };
  return (
    <div data-testid="purchase-orders-page">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div><label className="label-caps block mb-2">Stato</label>
          <select className="input-dark min-w-[180px]" value={status} onChange={e => setStatus(e.target.value)} data-testid="po-filter-status">
            <option value="In attesa">In attesa</option><option value="Ricevuto">Ricevuti</option><option value="Annullato">Annullati</option><option value="">Tutti</option>
          </select></div>
        <div className="ml-auto"><NewPurchaseOrderButton onDone={load}/></div>
      </div>
      <p className="text-xs opacity-60 mb-4 max-w-3xl">Gli ordini "In attesa" contano come merce in arrivo: non vengono più proposti nel Fabbisogno. Quando registri l'arrivo della merce puoi chiuderli direttamente da lì, oppure segnarli qui come ricevuti.</p>
      {listErr && <div className="text-red-600 text-sm mb-3" data-testid="po-list-error">{listErr}</div>}
      {!items ? <div className="text-sm opacity-60">Caricamento…</div> : items.length === 0 ?
        <div className="text-sm opacity-60 p-10 border border-dashed text-center" data-testid="po-list-empty">Nessun ordine.</div> : (
        <div className="card-dark overflow-x-auto">
          <table className="tbl w-full" data-testid="po-table">
            <thead><tr><th></th><th>Numero</th><th>Fornitore</th><th>Creato</th><th>Consegna prevista</th><th className="text-right">Totale</th><th>Stato</th><th></th></tr></thead>
            <tbody>
              {items.map(po => {
                const late = po.status === "In attesa" && po.expected_date && po.expected_date < new Date().toISOString().slice(0, 10);
                return (
                <Fragment key={po.id}>
                  <tr data-testid={`po-list-row-${po.id}`}>
                    <td className="w-6 cursor-pointer" onClick={() => setOpen({ ...open, [po.id]: !open[po.id] })} data-testid={`po-toggle-${po.id}`}>{open[po.id] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</td>
                    <td className="font-medium">{po.number}</td>
                    <td>{po.supplier_name}</td>
                    <td>{fmtDate(po.created_at)}</td>
                    <td className={late ? "text-red-600" : ""}>{po.expected_date ? fmtDate(po.expected_date) : "—"}{late ? " · in ritardo" : ""}</td>
                    <td className="text-right">{eur(po.total)}</td>
                    <td><Chip tone={TONE[po.status]}>{po.status}</Chip></td>
                    <td className="text-right whitespace-nowrap text-xs">
                      {po.status === "In attesa" ? <>
                        <button onClick={() => setSt(po, "Ricevuto")} className="btn-gold !py-1 !px-3 mr-2" data-testid={`po-receive-${po.id}`}>Segna ricevuto</button>
                        <button onClick={() => setSt(po, "Annullato", `Annullare l'ordine ${po.number}?`)} className="opacity-60 hover:opacity-100" data-testid={`po-cancel-${po.id}`}>Annulla</button>
                      </> : <button onClick={() => setSt(po, "In attesa")} className="opacity-60 hover:opacity-100" data-testid={`po-reopen-${po.id}`}>Riapri</button>}
                    </td>
                  </tr>
                  {open[po.id] && (
                    <tr data-testid={`po-detail-${po.id}`}><td></td><td colSpan={7} className="text-xs">
                      {po.lines.map(l => (
                        <div key={l.material_id} className="flex justify-between py-1 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                          <span>{l.name}</span><span>{n(l.quantity)} {l.unit} × {eur(l.unit_cost)}</span>
                        </div>
                      ))}
                      {po.notes && <div className="mt-2 opacity-70">Note: {po.notes}</div>}
                    </td></tr>
                  )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
